// Core packages
import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from '@angular/forms';
import { Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { Sort } from '@angular/material/sort';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from "@ngx-translate/core";
import { Workbook } from 'exceljs';
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { concat, Observable, of, Subject, Subscription } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take, tap } from "rxjs/operators";

// Core packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from 'src/app/modules/companies/companies.service';
import { PropertiesService } from 'src/app/modules/properties/properties.service';
import { RolesService } from 'src/app/modules/roles/roles.service';
import { SummaryDocsService } from 'src/app/modules/summary-docs/summary-docs.service';
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

import moment from 'moment';
import { SuppliersService } from 'src/app/modules/suppliers/suppliers.service';
import { UsersService } from 'src/app/modules/users/users.service';
import { Supplier } from 'src/app/shared/models/supplier/supplier.model';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import DocumentTypeStructure from "../../../properties/documentTypes";
import { SummaryDocsPropertiesDialogComponent } from "../../components/summary-docs-properties-dialog/summary-docs-properties-dialog.component";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";

@Component({
  selector: 'app-summary-docs-list',
  templateUrl: './summary-docs-list.page.html',
  styleUrls: ['./summary-docs-list.page.scss']
})
export class SummaryDocsListComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  loggedUserMaster = false;
  dateTimeFormat: DateTimeFormatPipe;
  activeSection: string; // Active document section
  activeSectionData: any[] = [];
  itemId: string; // Id of active mapping section item
  condoStructure: any;
  documentArchive: any;
  documentArchiveId: string;
  sortedDocuments: any[] = [];
  documentArchiveIsLoading = false;
  public documentTypeStructure: DocumentTypeStructure = new DocumentTypeStructure();

  summaryDocs: any[] = [];
  sortedData = [];
  filterValue = "";
  properties = [];
  adminUsers = [];
  selectedUser = "-1";
  // Empty on page open: the user must type to search a property (or pick "Tutti" from the list).
  // null = nothing selected; "-1" = "Tutti" (all); any other value = a specific property id.
  selectedProperty: string = null;
  // Immobili typeahead autocomplete: replaces the full properties download that ran on page open.
  // Options are fetched server-side only while the user types; the "Tutti" (all) option is always
  // appended so the default "-1" selection keeps working and no request is made on page load.
  propertyInput$ = new Subject<string>();
  propertyOptions$: Observable<any[]>;
  propertiesLoading = false;
  private allPropertiesOption = { _id: "-1", name: this.translateService.instant("common.words.all") };
  // Società typeahead autocomplete: same pattern as Immobili. Starts empty; "Tutti" always appended.
  // Selecting a company drives the dependent "Dipendenti" dropdown. null = nothing, "-1" = all, else _id.
  companyInput$ = new Subject<string>();
  companyOptions$: Observable<any[]>;
  companiesLoading = false;
  private allCompaniesOption = { _id: "-1", name: this.translateService.instant("common.words.all") };
  selectedCompanyId: string = null;
  // Dipendenti della società: dependent dropdown, disabled until a specific company is selected.
  // Populated from GET companies/{companyId}/sub-users; the option value is the employee userId.
  companyEmployees: any[] = [];
  employeesLoading = false;
  selectedSubUser = "-1";
  propertiesSelected = this.translateService.instant("properties.documents.summaryDocs.none");
  canExport = false;
  showParameters = true;
  areas: any;
  topics: any;
  subTopics: any;
  documents: any[] = [];
  selectedArea: string;
  selectedTopic: any;
  selectedSubTopics: any[] = [];
  selectedDocuments: any[] = [];
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  supplier: Supplier;
  previousSupplierId: string;
  selectedCompany: any;
  selectAll: any;

  // Dati aggiuntivi elenco/nuovo elemento
  suppliersName: any[] = [];
  yearsOfStorage: any[] = [];
  conductorName: any[] = [];
  nameInterventionArchiving: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private summaryDocsService: SummaryDocsService,
    private rolesService: RolesService,
    private suppliersService: SuppliersService,
    private helperService: HelperService,
    private userService: UsersService,
    private propertiesService: PropertiesService,
    private companiesService: CompaniesService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
  ) { }


  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.documentsPage.pageTitle")
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get logged user's role data
    this.currentRole = this.authService.loggedUserRequestedRoleType$.getValue();
    if (this.currentRole !== "admin" && this.currentRole !== "operator") {
      this.toastrService.error(this.translateService.instant("common.errors.not_authorized"));
      this.router.navigate([this.lang, 'app']);
    }

    // Immobili filter: server-side typeahead. No properties are downloaded on page open anymore
    // (the previous getSummaryDocsProperties() call fetched the full list of 5000+ properties).
    this.initPropertyAutocomplete();

    // Società filter: server-side typeahead (same pattern as Immobili). Drives the Dipendenti dropdown.
    this.initCompanyAutocomplete();

    this.subscriptions.push(
      this.summaryDocsService
        .getSummaryAdminUsers()
        .subscribe(
          res => {
            if (res.status) {
              this.adminUsers = res.data.items.map((el: any) => {
                return {
                  _id: el._id,
                  firstName: el.firstName,
                  lastName: el.lastName,
                  email: el.email
                }
              });
            }
          },
          err => {
            this.router.navigate([this.lang, "errors", "404"]);
          }
        )
    );

    this.initSupplierForm();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // Release the typeahead input subjects
    this.propertyInput$.complete();
    this.companyInput$.complete();
  }

  /**
   * init field form
   *
   * @since 1.0.0
   */
  initSupplierForm(): void {
    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: 'col-12 col-sm-8 col-md-6',
            type: 'datepicker',
            key: 'startDate',
            templateOptions: {
              label: this.translateService.instant('common.words.from'),
              placeholder: this.translateService.instant('properties.documents.summaryDocs.searchDate'),
              // max: this.model && this.model.endDate ? this.model.endDate : undefined,
            },
            // expressionProperties: {
            //   "templateOptions.max": (model: any) => {
            //     return model.endDate;
            //   },
            // }
          },
          {
            className: 'col-12 col-sm-8 col-md-6',
            type: 'datepicker',
            key: 'endDate',
            templateOptions: {
              label: this.translateService.instant('common.words.to'),
              placeholder: this.translateService.instant('properties.documents.summaryDocs.searchDate'),
              // min: this.model && this.model.startDate ? this.model.startDate : undefined,
            },
            // expressionProperties: {
            //   "templateOptions.min": "model.startDate",
            // }
          },
        ]
      }
    ];
  }

  /**
   * Init the "Immobili" filter as a server-side typeahead autocomplete.
   *
   * Nothing is fetched on page open: properties are searched (GET properties, search[general])
   * only while the user types. The "Tutti" (all) sentinel is always appended to the results so
   * that the default "-1" selection renders correctly and picking it triggers the full search
   * only when the user explicitly clicks "Cerca".
   *
   * @since 3.0.0
   */
  private initPropertyAutocomplete(): void {
    this.propertyOptions$ = concat(
      of([this.allPropertiesOption]),
      this.propertyInput$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => (this.propertiesLoading = true)),
        switchMap((term: string) => {
          if (!term || !term.trim()) {
            this.propertiesLoading = false;
            return of([this.allPropertiesOption]);
          }
          return this.propertiesService
            .getList(0, 20, "", "", { general: term })
            .pipe(
              map((res: ListApiResponseV2) => {
                let items: any[] = [];
                if (res && res.status && res.data && res.data.items) {
                  items = res.data.items;
                }
                // Always keep "Tutti" available below the search results.
                return items.concat([this.allPropertiesOption]);
              }),
              catchError((err: any) => {
                this.propertiesLoading = false;
                this.helperService.handleError(err);
                return of([this.allPropertiesOption]);
              }),
              tap(() => (this.propertiesLoading = false))
            );
        })
      )
    );
  }

  /**
   * Init the "Società" filter as a server-side typeahead autocomplete (mirrors initPropertyAutocomplete).
   *
   * Nothing is fetched on page open: companies are searched (GET companies, search[general]) only while
   * the user types. The "Tutti" (all) sentinel is always appended. Selecting a specific company triggers
   * onCompanyChange(), which reloads the dependent "Dipendenti" dropdown.
   *
   * @since 3.0.0
   */
  private initCompanyAutocomplete(): void {
    this.companyOptions$ = concat(
      of([this.allCompaniesOption]),
      this.companyInput$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => (this.companiesLoading = true)),
        switchMap((term: string) => {
          if (!term || !term.trim()) {
            this.companiesLoading = false;
            return of([this.allCompaniesOption]);
          }
          return this.companiesService
            .getList(0, 20, "", "", { general: term })
            .pipe(
              map((res: ListApiResponseV2) => {
                let items: any[] = [];
                if (res && res.status && res.data && res.data.items) {
                  items = res.data.items;
                }
                // Always keep "Tutti" available below the search results.
                return items.concat([this.allCompaniesOption]);
              }),
              catchError((err: any) => {
                this.companiesLoading = false;
                this.helperService.handleError(err);
                return of([this.allCompaniesOption]);
              }),
              tap(() => (this.companiesLoading = false))
            );
        })
      )
    );
  }

  /**
   * Reload the "Dipendenti" dropdown whenever the selected company changes.
   *
   * Employees belong to a company, so the dropdown is reset on every change and repopulated from
   * GET companies/{companyId}/sub-users. When no specific company is selected (empty or "Tutti"),
   * the dropdown stays empty and is disabled in the template. A high limit is passed on purpose:
   * the endpoint defaults to 10 items and paginates in-memory, so fetching all employees is cheap.
   *
   * @param company the company object emitted by the ng-select (change) event (may be null on clear)
   * @since 3.0.0
   */
  onCompanyChange(company: any): void {
    // Reset the dependent employee selection whenever the company changes.
    this.selectedSubUser = "-1";
    this.companyEmployees = [];

    const companyId = company && company._id ? company._id : null;
    if (!companyId || companyId === "-1") {
      return;
    }

    this.employeesLoading = true;
    this.subscriptions.push(
      this.companiesService.getSubUsersList(companyId, 0, 1000, "firstName", "asc").subscribe(
        res => {
          this.employeesLoading = false;
          if (res && res.status && res.data && res.data.items) {
            this.companyEmployees = res.data.items;
          }
        },
        err => {
          this.employeesLoading = false;
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Get current property data
   *
   * @since 1.0.0
   */
  getSupplierData(supplierId: string): void {
    if (supplierId === this.previousSupplierId) {
      return;
    }

    // Get requested supplier data
    this.previousSupplierId = supplierId;

    if (!supplierId) {
      // Reset data
      this.supplier = undefined;
      return;
    }

    this.subscriptions.push(
      this.suppliersService.getV2(supplierId).subscribe(
        res => {
          if (res.status) {
            this.supplier = new Supplier();
            this.supplier.setData(res.data);
            return;
          }
        },
        err => {
          // if we came here supplier do not exists
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Submit filter data and get results
   *
   * @since 1.0.0
   */
  applyFilter(): void {
    // Empty (null) or "Tutti" ("-1") both mean "no specific value selected" for the autocomplete filters.
    const noPropertySelected = !this.selectedProperty || this.selectedProperty === "-1";
    const noCompanySelected = !this.selectedCompanyId || this.selectedCompanyId === "-1";
    const noSubUserSelected = !this.selectedSubUser || this.selectedSubUser === "-1";
    const noParametersSelected = noPropertySelected
      && this.selectedUser === "-1"
      && noCompanySelected
      && noSubUserSelected
      && !(this.model && this.model.startDate)
      && !(this.model && this.model.endDate);

    if (noParametersSelected) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: this.translateService.instant("properties.documents.summaryDocs.confirmEmptySearchTitle"),
          message: this.translateService.instant("properties.documents.summaryDocs.confirmEmptySearchMessage"),
          btnOkText: this.translateService.instant("common.cta.confirm"),
          btnCancelText: this.translateService.instant("common.cta.close"),
        },
        width: "500px",
        disableClose: true,
      });
      dialogRef.beforeClosed().pipe(take(1)).subscribe(res => {
        if (res) {
          this.executeSearch();
        }
      });
      return;
    }

    this.executeSearch();
  }

  /**
   * Execute the summary docs search with the currently selected filters
   *
   * @since 1.0.0
   */
  private executeSearch(): void {
    let properties = "";
    if (this.selectedProperty === "-1") {
      // "Tutti" explicitly selected -> all properties.
      properties = "-1";
    } else if (this.selectedProperty) {
      // A specific property selected -> its _id.
      properties = this.selectedProperty;
    }
    // else: field left empty (not selected) -> send an empty string to the BE.
    // "Nessuno" must reach the BE as the explicit "none" sentinel: an empty string is
    // indistinguishable from "never touched" and the BE would drop the author filter,
    // showing the Archedi operators the user asked to exclude.
    let users = "";
    if (this.selectedUser === "none") {
      users = "none";
    } else if (this.selectedUser !== "-1") {
      users = this.selectedUser;
    } else {
      users = "-1";
    }
    // Company/employee are optional: send the id only when a specific value is selected,
    // otherwise leave empty so the service omits the param ("no filter").
    let company = "";
    if (this.selectedCompanyId && this.selectedCompanyId !== "-1") {
      company = this.selectedCompanyId;
    }
    // "Tutti" ("-1") is forwarded so the BE can expand it into the company employees;
    // it only means something relative to a company, so it is omitted without one.
    // "Nessuno" reaches the BE as the explicit "none" sentinel (same contract as the operators
    // filter) and means "insertions made by no employee of the selected company". Like "-1" it is
    // company-relative — the dropdown is disabled without a company — so it is omitted without one.
    let subUsers = "";
    if (this.selectedSubUser === "-1") {
      if (company) {
        subUsers = "-1";
      }
    } else if (this.selectedSubUser === "none") {
      if (company) {
        subUsers = "none";
      }
    } else if (this.selectedSubUser) {
      subUsers = this.selectedSubUser;
    }
    // Prepare filtering data
    const filterData = {
      properties: properties,
      users: users,
      company: company,
      subUsers: subUsers,
      startDate: this.model && this.model.startDate ? moment(this.model.startDate).local(true).toISOString() : undefined,
      endDate: this.model && this.model.endDate ? moment(this.model.endDate).local(true).toISOString() : undefined,
    };


    this.canExport = false;
    this.documentArchiveIsLoading = true;
    this.summaryDocs = [];
    this.subscriptions.push(
      this.summaryDocsService
        .getSummaryDocsGeneral(this.filterValue, filterData)
        .subscribe(res => {
          if (res.status) {
            if (res.data
              && Array.isArray(res.data)) {
              res.data.forEach((item: any) => {
                this.summaryDocs.push({
                  firstName: item.firstName,
                  lastName: item.lastName,
                  insertDate: item.insertDate,
                  company: item.company,
                  property: item.property,
                  insertTotalDoc: item.insertTotalDoc,
                  insertTotalPdf: item.insertTotalPdf,
                  deletedTotalDoc: item.deletedTotalDoc,
                  type: item.type
                });
              });
            }
            this.sortedData = this.summaryDocs.slice();
            if (Array.isArray(this.sortedData) && this.sortedData.length) {
              this.canExport = true;
              this.sortedData.sort((a, b) => {
                const firstNameA = a.firstName.toString();
                const firstNameB = b.firstName.toString();
                const lastNameA = a.lastName.toString();
                const lastNameB = b.lastName.toString();
                const companyA = a.company.toString();
                const companyB = b.company.toString();
                const propertyA = a.property.toString();
                const propertyB = b.property.toString();
                const insertDateA = moment(a.insertDate).format("YYYYMMDD").toString();
                const insertDateB = moment(b.insertDate).format("YYYYMMDD").toString();

                let comparison = 0;
                // if (createdByA > createdByB 
                //   && propertyIdA > propertyIdB
                //   && insertDateA > insertDateB) {
                //   comparison = 1;
                // } else if (createdByA > createdByB 
                //   && propertyIdA > propertyIdB
                //   && insertDateA < insertDateB) {
                //   comparison = -1;
                // } else if (createdByA > createdByB 
                //   && propertyIdA < propertyIdB
                //   && insertDateA < insertDateB) {
                //   comparison = -1;
                // } else if (createdByA < createdByB 
                //   && propertyIdA < propertyIdB
                //   && insertDateA < insertDateB) {
                //   comparison = -1;
                // }
                if (firstNameA === firstNameB
                  && lastNameA === lastNameB
                  && companyA === companyB
                  && propertyA === propertyB
                  && insertDateA > insertDateB) {
                  comparison = 1;
                } else if (firstNameA === firstNameB
                  && lastNameA === lastNameB
                  && companyA === companyB
                  && propertyA === propertyB
                  && insertDateA < insertDateB) {
                  comparison = -1;
                }
                return comparison;
              });
            }

            this.showParameters = false;
            this.documentArchiveIsLoading = false;
          } else {
            this.documentArchiveIsLoading = false;
            this.toastrService.error(res.message);
          }
        })
    );
  }

  /**
   * Handle documents filtering
   *
   * @since 1.0.0
   */
  filterDocuments(): void {
    const regex = new RegExp(this.filterValue.trim().toLowerCase(), "gi");
    this.documents.forEach((el: any) => {
      if ((el.title && el.title.search(regex) > -1) || el.code.search(regex) > -1) {
        el.visible = true;
      } else {
        el.visible = false;
        el.value = false;
      }
    });
  }

  /**
   * Handle table sort
   *
   * @since 1.0.0
   */
  sortData(sort: Sort): any {
    const data = this.sortedData;
    if (!sort.active || sort.direction === '') {
      this.sortedData = data;
      return;
    }

    this.sortedData = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'firstName': return this.compare(a.firstName, b.firstName, isAsc);
        case 'lastName': return this.compare(a.lastName, b.lastName, isAsc);
        case 'insertDate': return this.compare(a.insertDate, b.insertDate, isAsc);
        case 'company': return this.compare(a.company, b.company, isAsc);
        case 'property': return this.compare(a.property, b.property, isAsc);
        case 'insertTotalDoc': return this.compare(a.insertTotalDoc, b.insertTotalDoc, isAsc);
        case 'insertTotalPdf': return this.compare(a.insertTotalPdf, b.insertTotalPdf, isAsc);
        case 'deletedTotalDoc': return this.compare(a.deletedTotalDoc, b.deletedTotalDoc, isAsc);
        case 'type': return this.compare(a.type, b.type, isAsc);
        default: return 0;
      }
    });
  }

  /**
   * Utility per table sort
   *
   * @since 1.0.0
   */
  compare(a: number | string, b: number | string, isAsc: boolean): any {
    if (typeof a === "string") {
      a = a.toLowerCase();
    }
    if (typeof b === "string") {
      b = b.toLowerCase();
    }
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  /**
   * Utility: export results to excel
   *
   * @since 1.0.0
   */
  onExportToExcel(): void {
    const defaultColLength = 20;
    // let maxPropertyColLength = 0;
    // let maxTypeColLength = 0;
    // let maxContentColLength = 0;
    // let maxReferenceColLength = 0;
    // let maxDateTimeColLength = 0;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Sheet");
    const header = [
      this.translateService.instant('properties.documents.summaryDocs.firstName'),
      this.translateService.instant('properties.documents.summaryDocs.lastName'),
      this.translateService.instant('properties.documents.summaryDocs.insertDate'),
      this.translateService.instant('properties.documents.summaryDocs.company'),
      this.translateService.instant('properties.documents.summaryDocs.property'),
      this.translateService.instant('properties.documents.summaryDocs.insertTotalDoc'),
      this.translateService.instant('properties.documents.summaryDocs.insertTotalPdf'),
      this.translateService.instant('properties.documents.summaryDocs.deletedTotalDoc'),
      this.translateService.instant('properties.documents.summaryDocs.type')
    ];
    worksheet.addRow(header);
    worksheet.lastRow.height = 37.45; // Altezza riga 25
    worksheet.lastRow.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    worksheet.lastRow.font = {
      size: 7,
    };
    const widths = [30, 12, 20, 20, 15, 15, 15, 20];
    widths.forEach((val, index) => {
      worksheet.getColumn(index + 1).width = val;
    });

    worksheet.getRow(1).eachCell({ includeEmpty: true }, (cell) => {
      worksheet.getCell(cell.address).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF87CEEB" },
      };
    });

    // BEGIN - Print settings
    // Set horizontal page orientation
    worksheet.pageSetup = {
      orientation: "landscape",
      paperSize: 9, // A4
      showGridLines: true,
    };

    // Set page header/footer
    worksheet.headerFooter.oddHeader = `&LArchedi&RStampato il ${moment().format(
      "DD/MM/YYYY"
    )}`;
    worksheet.headerFooter.oddFooter = `&L${this.translateService.instant('properties.documents.summaryDocs.footerFileName')}&RPage &P/&N`;
    // END - Print settings

    let rowCount = 1;
    this.sortedData.forEach(el => {

      rowCount++;
      const row = worksheet.addRow([
        (el.firstName) ? el.firstName : "",
        (el.lastName) ? el.lastName : "",
        el.insertDate ? formatDate(el.insertDate, "dd/MM/yyyy", this.lang) : undefined,
        el.company,
        el.property,
        el.insertTotalDoc,
        el.insertTotalPdf,
        el.deletedTotalDoc,
        el.type,
      ]);
      worksheet.lastRow.font = {
        size: 7,
      };

      let maxLine = 1;
      for (let i = 1; i <= widths.length; i++) {
        if (i === 1) {
          worksheet.lastRow.getCell(i).alignment = {
            horizontal: "left",
            indent: 0,
            vertical: "middle",
            wrapText: true,
          };
        } else {
          worksheet.lastRow.getCell(i).alignment = {
            horizontal: "left",
            indent: 1,
            vertical: "middle",
            wrapText: true,
          };
        }

        const cellText = worksheet.lastRow.getCell(i).value;
        let value = -1;
        if (cellText) {
          value =
            Math.ceil(
              worksheet.lastRow.getCell(i).value.toString().length /
                widths[i - 1]
            ) - 1;
          const countLine = (
            worksheet.lastRow
              .getCell(i)
              .value.toString()
              .match(/(?:\r\n|\r|\n)/g) || []
          ).length;
          if (countLine > value) {
            value = countLine;
          }
        }
        if (value > maxLine) {
          maxLine = value;
        }
        worksheet.lastRow.height = 22.45 * maxLine; // 22.45 = altezza riga 15
      }
    });

    // Save to file
    const date = formatDate(new Date(), "yyyyMMddhhmmss", this.lang);
    const fileName = `${this.translateService.instant(
      "properties.documents.summaryDocs.exportFileName"
    )} ${date}.xlsx`;
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob(
        [data], 
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      FileSaver.saveAs(blob, fileName);
    });

    this.toastrService.success(this.translateService.instant('properties.documents.summaryDocs.exportedSuccessfully'));
  }

  /**
   * Handle click on properties select button
   *
   * @since 1.0.0
   */
  onPropertiesSelect(): void {
    const dialogRef = this.dialog.open(SummaryDocsPropertiesDialogComponent, {
      data: {
        modalTitle: "Elenco immobili",
        properties: this.properties,
        selectedCompany: this.selectedCompany,
        selectAll: this.selectAll,
      },
      width: "600px",
      minHeight: "50vh",
    });
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          } else {
            this.properties = res.propertiesSelected;
            this.selectedCompany = res.selectedCompany;
          }
        }
      );
  }

  // /**
  //  * Get selected area
  //  *
  //  * @since 1.0.0
  //  */
  // onChangeArea(): void {
  //   // Reset children
  //   this.topics = undefined;
  //   this.selectedTopic = undefined;
  //   this.subTopics = [];
  //   this.selectedSubTopics = [];
  //   this.documents = [];
  //   this.selectedDocuments = [];

  //   if (!this.selectedArea) {
  //     return;
  //   }

  //   // Get selected area content (es: A, B...)
  //   const selectedAreaContent = JSON.parse(JSON.stringify(
  //     this.documentTypeStructure.getDocumentArea(`${this.areas.indexOf(this.selectedArea) + 1}`)
  //   ));

  //   this.topics = selectedAreaContent ? selectedAreaContent.content : undefined;
  // }

  // /**
  //  * Get selected topic
  //  *
  //  * @since 1.0.0
  //  */
  // onChangeTopic(): void {
  //   // Reset children
  //   this.subTopics = [];
  //   this.selectedSubTopics = [];
  //   this.documents = [];
  //   this.selectedDocuments = [];

  //   if (!this.selectedTopic) {
  //     return;
  //   }

  //   // Get selected subTopic content (es: AA, AB...)
  //   const selectedTopicContent = JSON.parse(JSON.stringify(
  //     this.documentTypeStructure.getDocumentTopic(this.selectedTopic.code)
  //   ));

  //   this.subTopics = selectedTopicContent ? selectedTopicContent : undefined;

  //   // Sort data in alphabetical order
  //   this.subTopics.sort((a, b) => a.code.localeCompare(b.code));
  // }

  // /**
  //  * Add/remove selected subTopic
  //  *
  //  * @since 1.0.0
  //  */
  // onChangeSubTopic(subTopic: any, checked: boolean, parentCode: string): void {
  //   if (!subTopic) {
  //     this.toastrService.error("No subTopics injected");
  //     return;
  //   }

  //   if (checked) {
  //     // Add to displayed array
  //     // Get selected subTopic children (es: AA1, AA2...)
  //     this.documents = [
  //       ...this.documents,
  //       ...JSON.parse(JSON.stringify(this.documentTypeStructure.getDocumentSubTopic(subTopic.code, parentCode)))
  //     ];
  //     if (!this.selectedSubTopics.includes(subTopic)) {
  //       this.selectedSubTopics.push(subTopic);
  //     }

  //   } else if (!checked) {
  //     // Remove from arrays
  //     this.documents = this.documents.filter(doc => doc.parent !== subTopic.code);

  //     const index = this.selectedSubTopics.indexOf(subTopic);
  //     this.selectedSubTopics.splice(index, 1);
  //   }

  //   // Sort data in alphabetical order
  //   this.selectedSubTopics.sort((a, b) => a.code.localeCompare(b.code));

  // }

  /**
   * Add/remove selected document
   *
   * @since 1.0.0
   */
  onChangeDocument(document: string, checked: boolean, parentCode: string): void {
    if (!document) {
      this.toastrService.error("No documents injected");
      return;
    }

    if (checked) {
      // Add to displayed array
      if (!this.selectedDocuments.includes(document)) {
        this.selectedDocuments.push(document);
      }

    } else if (!checked) {
      // Remove from array
      const index = this.selectedDocuments.indexOf(document);
      this.selectedDocuments.splice(index, 1);
    }

    // Sort data in alphabetical order
    this.selectedDocuments.sort((a, b) => a.localeCompare(b));
  }
}
